const API_URL = process.env.NEXT_PUBLIC_AI_API_URL ?? 'http://localhost:3000';

export interface StoredMessage {
  id: string;
  role: string;
  parts: { type: string; text: string }[];
}

export interface ConversationItem {
  conversationId: string;
  title: string;
  updatedAt: string;
}

export interface LatestConversation {
  conversationId: string;
  messages: StoredMessage[];
}

export async function getConversations(accessToken: string): Promise<ConversationItem[]> {
  const res = await fetch(`${API_URL}/conversations`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch conversations');
  return res.json() as Promise<ConversationItem[]>;
}

export async function getConversation(
  id: string,
  accessToken: string,
): Promise<LatestConversation> {
  const res = await fetch(`${API_URL}/conversations/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch conversation');
  return res.json() as Promise<LatestConversation>;
}

export async function getLatestConversation(
  accessToken: string,
): Promise<LatestConversation> {
  const res = await fetch(`${API_URL}/conversations/latest`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch conversation');
  return res.json() as Promise<LatestConversation>;
}

export async function createConversation(
  accessToken: string,
): Promise<{ conversationId: string }> {
  const res = await fetch(`${API_URL}/conversations`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to create conversation');
  return res.json() as Promise<{ conversationId: string }>;
}

export async function saveConversationGreeting(
  id: string,
  accessToken: string,
  topic: 'ESTIMATE' | 'REVIEW',
): Promise<{ id: string; role: string; content: string }> {
  const res = await fetch(`${API_URL}/conversations/${id}/greeting`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ topic }),
  });
  if (!res.ok) throw new Error('Failed to save greeting');
  return res.json() as Promise<{ id: string; role: string; content: string }>;
}

export interface TraceData {
  intent: string;
  promptType: string;
  searchQuery: string | null;
  searchResults: Array<{
    treatmentName: string;
    categoryName: string;
    score: number;
    unit: string | null;
  }> | null;
  routerLatencyMs: number;
  searchLatencyMs: number | null;
  totalLatencyMs: number;
  inputTokens: number;
  outputTokens: number;
}

export interface DebugMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  trace: TraceData | null;
}

export interface DebugConversation {
  conversationId: string;
  messages: DebugMessage[];
}

export async function getDebugConversation(
  id: string,
  accessToken: string,
): Promise<DebugConversation> {
  const res = await fetch(`${API_URL}/conversations/${id}/debug`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch debug conversation');
  return res.json() as Promise<DebugConversation>;
}

export async function deleteConversation(
  id: string,
  accessToken: string,
): Promise<void> {
  const res = await fetch(`${API_URL}/conversations/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to delete conversation');
}
